import { useCallback, useEffect, useRef } from 'react'

import { useCloseTraceLogOnIneligibleIncident } from '../../../shared/hooks/useCloseTraceLogOnIneligibleIncident'
import { useLightMode } from '@/hooks/useLightMode'
import { useWindowSize } from '@react-hook/window-size'

import { tokenVar, useEscapeKey } from '@hivewatch/dark'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeTraceLogDrawer,
  selectIncidentTraceLogDrawerState,
} from '@/redux/ui/uiSlice'

import { INCIDENT_DRAWER_WIDTH } from '../../../shared/constants/drawerDimensions'
import { EventFiltersI } from '../../../shared/types/traceLogTypes'
import { IncidentDrawerContainer } from '../../components/IncidentDrawerContainer'
import { TraceLogContent } from './TraceLogContent'

export const TRACELOG_DRAWER_WIDTH = 445
export const DRAWER_BREAKPOINT = INCIDENT_DRAWER_WIDTH + TRACELOG_DRAWER_WIDTH

export const TraceLogDrawer = ({ incident }: EventFiltersI) => {
  const isOpen = useAppSelector(selectIncidentTraceLogDrawerState)?.isOpen
  const dispatch = useAppDispatch()
  const [width] = useWindowSize()
  const mode = useLightMode()
  const isDark = mode === 'dark'
  // tokenVar resolves the dark-package semantic to its CSS var + fallback —
  // this container renders outside the DarkProvider, so a raw token name
  // would not resolve here.
  const drawerBg = isDark ? tokenVar('colors.bg.card') : 'white'

  useEffect(() => {
    if (width < DRAWER_BREAKPOINT) {
      dispatch(closeTraceLogDrawer())
    }
  }, [width])

  useCloseTraceLogOnIneligibleIncident({ incident })

  // Escape closes this sub-drawer only — the incident drawer behind it stays
  // open. Joins the dark package's escape stack so nesting resolves. The ref
  // scopes the foreign-dialog guard: a Chakra v1 dialog opened from the action
  // bar (Emergency Call) traps focus without joining the stack, and must keep
  // Escape rather than have it close this drawer behind it. ENG-32100
  const containerRef = useRef<HTMLDivElement>(null)
  const handleEscape = useCallback(
    () => dispatch(closeTraceLogDrawer()),
    [dispatch]
  )
  useEscapeKey(handleEscape, Boolean(isOpen), containerRef)

  return (
    isOpen && (
      <IncidentDrawerContainer
        bgColor={drawerBg}
        borderColor={
          isDark
            ? tokenVar('colors.border.subtle')
            : tokenVar('colors.light.border.subtle')
        }
        containerRef={containerRef}
        dataTestid='TraceLogDrawer:wrapper'
        width={TRACELOG_DRAWER_WIDTH}
      >
        <TraceLogContent incident={incident} />
      </IncidentDrawerContainer>
    )
  )
}
